import InstrumentInfoContent from '@/components/common/InstrumentInfoContent';

import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { InstrumentConfig } from '@/state/reducers/ohoSlice';
import { removeSubscription, TraderMarket } from '@/state/reducers/traderMarketSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { ListItemProps } from '@/utils/hooks/useMarketList';
import { Combobox, Transition } from '@headlessui/react';
import cn from 'classnames';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { MdExpandMore, MdInfoOutline, MdOutlineRemoveCircle, MdSearch } from 'react-icons/md';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

export interface Option {
    label: string;
    value?: string;
    market?: TraderMarket;
    config?: InstrumentConfig;
    added?: boolean;
}

interface InstrumentSelectorProps {
    options: Option[];
    selected?: Option;
    watchListId: string;
    parentSize?: DOMRectReadOnly;
    onSubmit: (e: TraderMarket) => void;
    onRemove: (e: TraderMarket) => void;
}

const InstrumentSelector = (props: InstrumentSelectorProps) => {
    const { options, selected, parentSize, watchListId, onSubmit, onRemove } = props;
    const lang = useLanguage();
    const dispatch = useAppDispatch();
    const accountType = useAppSelector(selectAccountType);

    const [query, setQuery] = useState<any>('');
    const [categoryFilter, setCategoryFilter] = useState<any>('All');
    const [instrumentInfoOption, setInstrumentInfoOption] = useState<Option>();

    const categoryOptions = useMemo(() => {
        return [
            { label: lang.common.showAll, value: 'All' },
            { label: lang.common.cryptoCFD, value: 'Crypto' },
            { label: lang.common.fxSpot, value: 'FX' },
            { label: lang.common.indexCFD, value: 'Index' },
            { label: lang.common.commCFD, value: 'Commodity' },
            { label: lang.common.futuresCFD, value: 'Future' }
        ];
    }, [lang]);

    const filteredOptions = useMemo(() => {
        if (query) {
            const temp = query.replace('/', '').toUpperCase();
            return options
                .filter((option) => option.label.replace('/', '').includes(temp))
                .filter((option) => option.config?.type === categoryFilter || categoryFilter === 'All');
        }
        return options.filter((option) => option.config?.type === categoryFilter || categoryFilter === 'All');
    }, [options, query, categoryFilter]);

    const filteredCategoryOptions = useMemo(() => {
        return categoryOptions.filter((category) => {
            const hasInCategory = options.some((option) => option.config?.type === category.value);

            return category.value === 'All' || hasInCategory;
        });
    }, [options]);

    const { shouldFullWidth, shouldAutoWidth } = useMemo(() => {
        if (!parentSize) return { shouldFullWidth: false, shouldAutoWidth: false };
        else if (parentSize.width >= 300 && parentSize.width < 580)
            return { shouldFullWidth: true, shouldAutoWidth: false };
        else if (parentSize.width < 300) return { shouldFullWidth: true, shouldAutoWidth: true };
        return { shouldFullWidth: false, shouldAutoWidth: false };
    }, [parentSize]);

    const VirtualizedUserMarket = useCallback(
        ({ index, style }: ListItemProps) => {
            const option = filteredOptions[index];
            const type = option.config?.type;

            return (
                <Combobox.Option
                    style={style}
                    className={cn('relative flex items-center justify-between select-none py-2 pl-3 pr-4 group ', {
                        'hover:bg-brand-primary text-brand-text cursor-pointer': !option.added,
                        'text-brand-neutral-400': option.added
                    })}
                    onClick={() => {
                        if (!option.added) {
                            if (option.market) onSubmit(option.market);
                        }
                    }}
                    value={option}>
                    <div>
                        <div className="flex items-center gap-1">
                            {/* <InstrumentInfoModal market={option?.market} /> */}
                            <div
                                title="Instrument Info"
                                className={cn('h-full flex items-center p-1 cursor-pointer')}
                                onClick={() => {
                                    setInstrumentInfoOption(option);
                                }}>
                                <MdInfoOutline className="w-3 h-3" />
                            </div>
                            <span className="block truncate text-sm">{option?.label}</span>
                        </div>
                        <span
                            className={cn('text-xs text-brand-neutral-400', {
                                'group-hover:text-brand-text': !option.added
                            })}>
                            {type === 'FX' && lang.common.fxSpot}
                            {type === 'Crypto' &&
                                `${accountType === 'NOP' ? lang.common.cryptoSpot : lang.common.cryptoCFD}`}
                            {type === 'Index' && lang.common.indexCFD}
                            {type === 'Commodity' && lang.common.commCFD}
                            {type === 'Future' && lang.common.futuresCFD}
                            {!type && '-'}
                        </span>
                    </div>
                    {option.added && (
                        <span
                            className="cursor-pointer"
                            onClick={() => {
                                onRemove(option.market as any);
                                dispatch(
                                    removeSubscription({
                                        id: watchListId,
                                        market: option.market as any
                                    })
                                );
                            }}>
                            <MdOutlineRemoveCircle className="w-5 h-5 text-brand-red hover:text-brand-red-light" />
                        </span>
                    )}
                </Combobox.Option>
            );
        },
        [lang, filteredOptions]
    );

    const getItemSize = useCallback((index: number) => 56, []);

    const VirtualizedList = useCallback(
        () => (
            <AutoSizer>
                {({ height, width }) => (
                    <VariableSizeList
                        height={height}
                        width={width}
                        itemCount={filteredOptions.length}
                        itemSize={getItemSize}>
                        {VirtualizedUserMarket}
                    </VariableSizeList>
                )}
            </AutoSizer>
        ),
        [filteredOptions, VirtualizedUserMarket]
    );

    return (
        <Combobox value={[]} multiple>
            <div
                className={cn('relative', {
                    'w-full': shouldFullWidth
                })}>
                <div className="relative w-full cursor-default overflow-hidden bg-brand-background-dark border border-brand-neutral-700 hover:border-brand-neutral-600 sm:text-xs">
                    <div
                        className={cn('absolute inset-y-0 flex items-center', {
                            'left-0 pl-1': lang.dir === 'ltr',
                            'right-0 pr-1': lang.dir === 'rtl'
                        })}>
                        <MdSearch className="h-5 w-5 text-brand-neutral-400" aria-hidden="true" />
                    </div>
                    <Combobox.Input
                        className={cn(
                            'w-full border-none py-1 text-xs leading-5 text-brand-text bg-brand-background-dark outline-none placeholder:text-brand-neutral-500',
                            {
                                'pl-7 pr-10': lang.dir === 'ltr',
                                'pl-10 pr-7': lang.dir === 'rtl'
                            }
                        )}
                        autoComplete="off"
                        autoCorrect="off"
                        placeholder={selected?.label}
                        onChange={(event) => setQuery(event.target.value)}
                    />
                    <Combobox.Button>
                        <div
                            className={cn('absolute inset-y-0 flex items-center', {
                                'right-0 pr-1': lang.dir === 'ltr',
                                'left-0 pl-1': lang.dir === 'rtl'
                            })}>
                            <MdExpandMore className="h-5 w-5 text-brand-neutral-400" aria-hidden="true" />
                        </div>
                    </Combobox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setInstrumentInfoOption(undefined)}>
                    <Combobox.Options
                        className={cn(
                            'absolute mt-2 z-[60] flex flex-wrap xsm:flex-nowrap cursor-default bg-brand-background-dark border border-brand-neutral-700 text-sm shadow-lg shadow-brand-neutral-900 overflow-clip min-h-[200px]',
                            {
                                'right-0': lang.dir === 'ltr',
                                'left-0': lang.dir === 'rtl'
                            }
                        )}>
                        {!instrumentInfoOption && (
                            <div className="flex flex-col text-xs w-fit border-r border-brand-neutral-600">
                                {filteredCategoryOptions.map((option, optionIdx) => {
                                    return (
                                        <div
                                            key={optionIdx}
                                            className={cn(
                                                'cursor-pointer whitespace-nowrap hover:bg-brand-primary p-2',
                                                {
                                                    'bg-brand-primary': categoryFilter === option.value
                                                }
                                            )}
                                            onClick={() => setCategoryFilter(option.value)}>
                                            {option.label}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {instrumentInfoOption && (
                            <div className="w-full xsm:w-auto border-b xsm:border-b-0 xsm:border-r border-brand-neutral-700 text-xs whitespace-nowrap flex flex-row xsm:flex-col">
                                <div className="p-2 xsm:bg-brand-primary">Instrument Info</div>
                                <div className="py-2 xsm:hidden">-</div>
                                <div className="p-2 xsm:mt-[1px]">{instrumentInfoOption.label}</div>
                            </div>
                        )}
                        <div
                            className={cn('flex flex-col', {
                                'w-[203px]': !shouldAutoWidth && !instrumentInfoOption,
                                'w-[140px]': shouldAutoWidth && !instrumentInfoOption,
                                'w-full': instrumentInfoOption
                            })}>
                            {instrumentInfoOption && (
                                <InstrumentInfoContent
                                    watchListId={watchListId}
                                    option={instrumentInfoOption}
                                    onClose={() => setInstrumentInfoOption(undefined)}
                                    onSubmit={onSubmit}
                                    onRemove={onRemove}
                                    flexWrap
                                />
                            )}
                            {!instrumentInfoOption && (
                                <Fragment>
                                    {filteredOptions.length === 0 && query !== '' ? (
                                        <div className="relative select-none py-2 px-4 text-brand-text">
                                            Nothing found.
                                        </div>
                                    ) : (
                                        <VirtualizedList />
                                    )}
                                </Fragment>
                            )}
                        </div>
                    </Combobox.Options>
                </Transition>
            </div>
        </Combobox>
    );
};

export default InstrumentSelector;
